import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success(t('contact.form.success'));
    } catch {
      toast.error(lang === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 lg:pt-24 pb-20" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl font-bold text-[#1a1a1a] tracking-tight"
          >
            {t('contact.title')}
          </motion.h1>
          <div className="w-16 h-1 mx-auto mt-4" style={{ backgroundColor: '#f4d00d' }} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info + Map */}
          <div>
            <div className="space-y-6 mb-8">
              <a href="https://maps.google.com/?q=153+Champlain+Saint-Jean-sur-Richelieu" target="_blank" rel="noopener noreferrer"
                className="flex items-start gap-4 p-4 rounded-xl bg-white border border-[#e0e0e0] hover:shadow-md transition-shadow" data-testid="contact-address">
                <MapPin className="w-6 h-6 text-[#e53a3a] shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-[#1a1a1a] mb-1">{lang === 'fr' ? 'Adresse' : 'Address'}</h3>
                  <p className="text-sm text-[#555555]">{t('contact.address')}</p>
                </div>
              </a>
              <a href="tel:+14503478635" className="flex items-start gap-4 p-4 rounded-xl bg-white border border-[#e0e0e0] hover:shadow-md transition-shadow" data-testid="contact-phone">
                <Phone className="w-6 h-6 text-[#e53a3a] shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-[#1a1a1a] mb-1">{lang === 'fr' ? 'Téléphone' : 'Phone'}</h3>
                  <p className="text-sm text-[#555555]">{t('contact.phone')}</p>
                </div>
              </a>
              <a href="mailto:info@restobelgo.com" className="flex items-start gap-4 p-4 rounded-xl bg-white border border-[#e0e0e0] hover:shadow-md transition-shadow" data-testid="contact-email">
                <Mail className="w-6 h-6 text-[#e53a3a] shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-[#1a1a1a] mb-1">{lang === 'fr' ? 'Courriel' : 'Email'}</h3>
                  <p className="text-sm text-[#555555]">info@restobelgo.com</p>
                </div>
              </a>
              <div className="flex items-start gap-4 p-4 rounded-xl bg-white border border-[#e0e0e0]" data-testid="contact-hours">
                <Clock className="w-6 h-6 text-[#e53a3a] shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-[#1a1a1a] mb-1">{t('contact.hours')}</h3>
                  <p className="text-sm text-[#555555]">Mer-Ven: 11h-19h/22h | Sam-Dim: 11h-20h/21h</p>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="rounded-xl overflow-hidden border border-[#e0e0e0] shadow-sm" data-testid="google-map">
              <iframe
                title="Resto Belgo Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2818.3!2d-73.263!3d45.308!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDE4JzI4LjgiTiA3M8KwMTUnNDYuOCJX!5e0!3m2!1sfr!2sca!4v1"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div>
            {submitted ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16" data-testid="contact-success">
                <CheckCircle className="w-16 h-16 text-[#2E7D32] mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-semibold mb-2">{t('contact.form.success')}</h3>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-[#e0e0e0] p-8 space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label>{t('checkout.name')}</Label>
                    <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="mt-1" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <Label>{t('checkout.email')}</Label>
                    <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required className="mt-1" data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label>{t('checkout.phone')}</Label>
                    <Input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <Label>{t('contact.form.subject')}</Label>
                    <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="mt-1" data-testid="contact-subject-input" />
                  </div>
                </div>
                <div>
                  <Label>{t('contact.form.message')}</Label>
                  <Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} required rows={5} className="mt-1" data-testid="contact-message-input" />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-belgo w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white font-semibold"
                  style={{ backgroundColor: '#e53a3a' }}
                  data-testid="contact-send-btn"
                >
                  <Send className="w-4 h-4" /> {loading ? '...' : t('contact.form.send')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
